/**
 * Notification Route & Action Resolver
 *
 * Single source of truth for mapping notification types to frontend routes
 * or in-context modal actions. The frontend owns all routing decisions --
 * the backend only provides structured data (type + data + groupKey).
 *
 * resolveNotificationAction() returns an action object:
 *   - { type: 'modal', modal } → open an in-context modal (no navigation)
 *   - { type: 'navigate', route } → navigate to the route
 *   - null → no action
 *
 * resolveNotificationRoute() is kept for backward compatibility and returns
 * only the route string.
 *
 * @module entities/notification/utils/resolveNotificationRoute
 */

import type { Notification } from '../types';

// ── Action Types ─────────────────────────────────────────────────

/** Modals that can be opened from notification clicks */
export type OnboardingModal = 'phone' | 'identity-verification' | 'two-factor-setup';

/** Action returned by resolveNotificationAction */
export type NotificationAction =
  | { type: 'navigate'; route: string }
  | { type: 'modal'; modal: OnboardingModal }
  | null;

// ── Route Context ─────────────────────────────────────────────────
// User notifications navigate with locale prefix (useLocaleNavigate).
// Admin notifications navigate within /admin/* (useNavigate).

type RouteContext = 'user' | 'admin';

// ── Data Helpers ──────────────────────────────────────────────────

/** Safely extract a string field from the notification data payload */
function getDataString(data: Record<string, unknown> | null, key: string): string | null {
  if (!data || typeof data[key] !== 'string') return null;
  return data[key] as string;
}

// ── Onboarding Modal Map ─────────────────────────────────────────
// groupKey → modal: nudges that can be completed via in-context modal.
// Nudges not in this map fall through to route-based navigation.

const ONBOARDING_MODAL_MAP: Partial<Record<string, OnboardingModal>> = {
  'onboarding:verify-phone': 'phone',
  'onboarding:verify-identity': 'identity-verification',
  'onboarding:enable-mfa': 'two-factor-setup',
};

// ── Onboarding Route Map ─────────────────────────────────────────
// Backend onboarding nudges use short actionUrl paths (/settings/security,
// /verification, /wallet/setup) that don't match frontend routes.
// groupKey provides deterministic identification per nudge type.

const ONBOARDING_ROUTE_MAP: Record<string, string> = {
  '/settings/security': '/dashboard/profile?tab=security',
  '/verification': '/dashboard/profile?tab=security',
  '/wallet/setup': '/dashboard/wallet/setup',
};

/** Default route for unrecognized onboarding nudges */
const ONBOARDING_FALLBACK = '/dashboard/profile?tab=security';

// ── User Route Map ────────────────────────────────────────────────

/** Resolve a user-facing notification to a frontend route path */
function resolveUserRoute(type: string, data: Record<string, unknown> | null, actionUrl: string | null): string | null {
  const projectId = getDataString(data, 'projectId');

  switch (type) {
    // Crowdfunding: creator project management
    case 'PROJECT_APPROVED':
    case 'PROJECT_REJECTED':
    case 'PROJECT_FUNDED':
    case 'PROJECT_PARTIALLY_FUNDED':
    case 'PROJECT_EXPIRED':
    case 'PROJECT_SUSPENDED':
    case 'NEW_PLEDGE':
    case 'PLEDGE_CANCELLED':
      if (projectId) return `/dashboard/launchpad/${projectId}/manage`;
      break;

    // Crowdfunding: public project page (needs slug, fallback to actionUrl)
    case 'NEW_COMMENT':
    case 'PROJECT_UPDATE': {
      const slug = getDataString(data, 'slug') || getDataString(data, 'projectSlug');
      if (slug) return `/launchpad/${slug}`;
      if (projectId) return `/dashboard/launchpad/${projectId}/manage`;
      break;
    }

    // Crowdfunding: backer's own pledges
    case 'PLEDGE_REFUNDED':
    case 'PLEDGE_CAPTURED':
      return '/dashboard/launchpad/backings';

    // Onboarding nudges: map backend actionUrl to correct dashboard route
    case 'ONBOARDING_NUDGE':
      return (actionUrl && ONBOARDING_ROUTE_MAP[actionUrl]) ?? ONBOARDING_FALLBACK;

    // Add future notification types here
    // case 'CAMPAIGN_APPROVED':
    // case 'DONATION_RECEIVED':
  }

  return null;
}

// ── Admin Route Map ───────────────────────────────────────────────

/** Resolve an admin notification to an admin panel route */
function resolveAdminRoute(type: string, data: Record<string, unknown> | null): string | null {
  const projectId = getDataString(data, 'projectId');

  switch (type) {
    // Crowdfunding: admin review
    case 'PROJECT_PENDING_REVIEW':
    case 'PROJECT_FLAGGED':
    case 'ESCROW_ACTION_NEEDED':
      if (projectId) return `/admin/crowdfunding?project=${projectId}`;
      return '/admin/crowdfunding';

    // Add future admin notification types here
    // case 'CAMPAIGN_PENDING_REVIEW':
  }

  return null;
}

// ── Public API ────────────────────────────────────────────────────

/**
 * Resolves a notification to a frontend route path.
 *
 * Uses the notification's `type` and `data` fields to construct the correct
 * route, independent of the backend's `actionUrl` field. Falls back to
 * `actionUrl` for unrecognized notification types.
 *
 * @param notification - The notification to resolve
 * @param context - Whether this is a user or admin context
 * @returns The resolved route path, or null if no route can be determined
 *
 * @example
 * // User notification
 * resolveNotificationRoute(notification, 'user')
 * // → "/dashboard/launchpad/f87ed23f/manage"
 *
 * // Admin notification
 * resolveNotificationRoute(notification, 'admin')
 * // → "/admin/crowdfunding?project=f87ed23f"
 */
export function resolveNotificationRoute(
  notification: Notification,
  context: RouteContext
): string | null {
  const { type, data, actionUrl } = notification;

  // Try type-based resolution first
  const resolved = context === 'admin'
    ? resolveAdminRoute(type, data)
    : resolveUserRoute(type, data, actionUrl);

  if (resolved) return resolved;

  // Fallback to actionUrl for notification types we haven't mapped yet
  return actionUrl ?? null;
}

/**
 * Resolves a notification to an action: either open a modal or navigate.
 *
 * For onboarding nudges with a known groupKey, returns a modal action so
 * the user can complete the task in-place without navigating away.
 * For everything else, falls back to route-based navigation.
 *
 * @param notification - The notification to resolve
 * @param context - Whether this is a user or admin context
 * @returns Action object (modal or navigate), or null if no action
 *
 * @example
 * const action = resolveNotificationAction(notification, 'user');
 * if (action?.type === 'modal') openModal(action.modal);
 * if (action?.type === 'navigate') navigate(action.route);
 */
export function resolveNotificationAction(
  notification: Notification,
  context: RouteContext
): NotificationAction {
  // Check if this onboarding nudge can be handled by an in-context modal
  if (notification.type === 'ONBOARDING_NUDGE' && notification.groupKey) {
    const modal = ONBOARDING_MODAL_MAP[notification.groupKey];
    if (modal) return { type: 'modal', modal };
  }

  // Fall back to route-based navigation
  const route = resolveNotificationRoute(notification, context);
  if (route) return { type: 'navigate', route };

  return null;
}

export default resolveNotificationRoute;
